import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Play, MapPin, ExternalLink, Globe, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { getHomepageSectionML } from '../services/api';

const CaseStudyPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => { loadProject(); }, [slug, language]);

  const loadProject = async () => {
    setLoading(true);
    const normalizedLang = ['pt','en','fr','ar','zh'].includes(language) ? language : 'en';
    const result = await getHomepageSectionML('projects', normalizedLang);
    if (result.success) {
      const found = (result.data.projects || []).find(p => p.link === `/projects/${slug}`);
      setProject(found || null);
    }
    setLoading(false);
  };

  const getVideoId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&?]+)/);
    return m ? m[1] : null;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-[#6C757D]">Loading...</div></div>;
  if (!project) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-[#6C757D]">Project not found</p>
      <Button onClick={() => navigate('/')}>Back to Home</Button>
    </div>
  );

  const videoId = getVideoId(project.video_url);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-[60vh] overflow-hidden bg-[#0a0a0a]">
        {showVideo && videoId ? (
          <iframe src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`} title={project.title}
            className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
        ) : (
          <>
            <img src={project.image} alt={project.title} className="w-full h-full object-contain" style={{ animation: 'caseZoom 10s ease-out forwards' }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
            {project.featured && (
              <div className="absolute top-6 left-6 bg-[#FF6B35] text-white text-sm font-bold px-4 py-2 rounded-full flex items-center gap-2 z-10">
                <Star className="w-4 h-4 fill-white" /> TOP PROJECT
              </div>
            )}
            {videoId && (
              <button onClick={() => setShowVideo(true)} className="absolute inset-0 flex items-center justify-center z-10">
                <div className="w-20 h-20 bg-[#FF6B35] rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
                  <Play className="w-8 h-8 text-white fill-white ml-1" />
                </div>
              </button>
            )}
            <div className="absolute bottom-0 left-0 right-0 p-8 z-10">
              <div className="container mx-auto max-w-4xl">
                <span className="text-xs font-bold uppercase tracking-wider text-[#48CAE4] bg-[#023E8A] px-3 py-1 rounded">{project.type}</span>
                <h1 className="text-3xl md:text-5xl font-bold text-white mt-4 mb-2">{project.title}</h1>
                <p className="text-white/70 flex items-center gap-2"><MapPin className="w-4 h-4" /> {project.location}</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <div className="container mx-auto max-w-4xl px-6 py-12">
        <button onClick={() => navigate('/')} className="flex items-center text-[#6C757D] hover:text-[#2B2D42] mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </button>

        {/* Website CTA Banner */}
        {project.website_url && (
          <a href={project.website_url} target="_blank" rel="noopener noreferrer"
            className="block mb-12 group">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#023E8A] via-[#0077B6] to-[#00B4D8] p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.01]">
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Globe className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm font-medium uppercase tracking-wider">Official Website</p>
                    <p className="text-white text-xl font-bold">{project.website_label || project.website_url}</p>
                    <p className="text-[#48CAE4] text-sm mt-1">{project.website_url}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white text-[#023E8A] font-bold px-6 py-3 rounded-xl group-hover:bg-[#FF6B35] group-hover:text-white transition-all duration-300 shadow-lg">
                  Visit Website <ExternalLink className="w-4 h-4" />
                </div>
              </div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>
          </a>
        )}

        {/* Highlights */}
        {project.highlights && project.highlights.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {project.highlights.map((h, i) => (
              <div key={i} className="bg-[#023E8A] text-white rounded-xl p-5 text-center hover:bg-[#012a5e] transition-colors">
                <p className="text-3xl font-bold text-[#48CAE4]">{h.value}</p>
                <p className="text-xs text-white/70 mt-1">{h.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Description */}
        <div className="mb-12">
          <p className="text-lg text-[#6C757D] leading-relaxed">{project.description}</p>
        </div>

        {/* Full Case Study */}
        {project.case_study && (
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-[#2B2D42] mb-6 pb-3 border-b-2 border-[#00B4D8]">Full Story</h3>
            {project.case_study.split('\n').map((paragraph, i) => (
              paragraph.trim() ? <p key={i} className="text-[#2B2D42] leading-relaxed mb-4 text-base">{paragraph}</p> : null
            ))}
          </div>
        )}

        {/* Video Section */}
        {videoId && (
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-[#2B2D42] mb-4">Watch Documentary</h3>
            <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
              <iframe src={`https://www.youtube.com/embed/${videoId}`} title={project.title}
                className="w-full h-full" allow="encrypted-media" allowFullScreen />
            </div>
          </div>
        )}

        {/* Bottom Website CTA */}
        {project.website_url && (
          <div className="text-center py-12 border-t border-gray-200">
            <p className="text-[#6C757D] mb-4">Discover more about this project</p>
            <a href={project.website_url} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#FF6B35] hover:bg-[#E5562D] text-white px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all hover:scale-105">
                <Globe className="w-5 h-5 mr-3" />
                {project.website_label || 'Visit Official Website'}
                <ArrowRight className="w-5 h-5 ml-3" />
              </Button>
            </a>
            <p className="text-sm text-[#ADB5BD] mt-3">{project.website_url}</p>
          </div>
        )}
      </div>

      <style>{`@keyframes caseZoom { 0% { transform: scale(1); } 100% { transform: scale(1.05); } }`}</style>
    </div>
  );
};

export default CaseStudyPage;
